import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FolderOpen, ArrowLeft, FileCheck, Upload, Trash2, AlertCircle, Clock, CheckCircle2, XCircle, FileText, Car, Shield, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import axios from "axios";



const DOC_TYPES = [
  { value: "license", label: "Driver's License", icon: CreditCard, desc: "Valid government-issued driver's license" },
  { value: "vehicle_registration", label: "Vehicle Registration", icon: Car, desc: "Current vehicle registration certificate" },
  { value: "insurance", label: "Insurance Document", icon: Shield, desc: "Proof of vehicle or liability insurance" },
  { value: "other", label: "Other Document", icon: FileText, desc: "Additional supporting documents" },
];

const STATUS_CONFIG = {
  pending: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock, label: "Pending Review" },
  approved: { color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2, label: "Approved" },
  rejected: { color: "bg-red-100 text-red-800 border-red-200", icon: XCircle, label: "Rejected" },
};

const ProfessionalDocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedType, setSelectedType] = useState("license");
  const [docName, setDocName] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/professional/documents`, { withCredentials: true });
      setDocuments(res.data.documents || []);
    } catch { setDocuments([]); }
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      // Upload file first
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/upload/document`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const fileUrl = uploadRes.data.url || uploadRes.data.file_url || "";

      // Save document record
      await axios.post(`${API}/professional/documents`, {
        doc_type: selectedType,
        doc_name: docName || file.name,
        file_url: fileUrl,
        expiry_date: expiryDate || null,
      }, { withCredentials: true });

      toast.success("Document uploaded successfully");
      setShowUpload(false);
      setFile(null);
      setDocName("");
      setExpiryDate("");
      fetchDocuments();
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    }
    setUploading(false);
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/professional/documents/${docId}`, { withCredentials: true });
      toast.success("Document removed");
      fetchDocuments();
    } catch { toast.error("Failed to delete"); }
  };

  const getDocsByType = (type) => documents.filter((d) => d.doc_type === type);

  // Check for expiring documents (within 30 days)
  const expiringDocs = documents.filter((d) => {
    if (!d.expiry_date) return false;
    const exp = new Date(d.expiry_date);
    const daysLeft = (exp - new Date()) / (1000 * 60 * 60 * 24);
    return daysLeft > 0 && daysLeft <= 30;
  });

  return (
    <div className="min-h-screen bg-stone-50" data-testid="professional-documents-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2">
              <FolderOpen className="w-6 h-6 text-emerald-600" /> Documents Manager
            </h1>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" onClick={() => setShowUpload(!showUpload)} data-testid="upload-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Upload
            </Button>
          </div>

          {/* Expiry Reminders */}
          {expiringDocs.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6" data-testid="expiry-warning">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <p className="font-semibold text-amber-800 text-sm">Expiring Soon</p>
              </div>
              {expiringDocs.map((d) => (
                <p key={d.doc_id} className="text-xs text-amber-700 ml-7">{d.doc_name} expires {new Date(d.expiry_date).toLocaleDateString()}</p>
              ))}
            </div>
          )}

          {/* Upload Form */}
          {showUpload && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="bg-white rounded-2xl border border-stone-100 p-6 mb-6" data-testid="upload-form">
              <h3 className="font-semibold text-emerald-950 mb-4">Upload New Document</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Document Type</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {DOC_TYPES.map((dt) => (
                      <button key={dt.value} onClick={() => setSelectedType(dt.value)} className={`p-3 rounded-xl border text-left transition-all ${selectedType === dt.value ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`} data-testid={`doc-type-${dt.value}`}>
                        <dt.icon className={`w-4 h-4 mb-1 ${selectedType === dt.value ? "text-emerald-600" : "text-stone-400"}`} />
                        <p className="text-xs font-medium">{dt.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Document Name</Label>
                  <Input value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="e.g., Ontario Driver's License" data-testid="doc-name-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Expiry Date (Optional)</Label>
                  <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} data-testid="expiry-date-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">File</Label>
                  <Input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0])} data-testid="file-input" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowUpload(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleUpload} disabled={uploading || !file} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-upload-btn">
                    {uploading ? "Uploading..." : "Upload Document"}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Documents by Type */}
          {loading ? (
            <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => (<div key={i} className="bg-white rounded-2xl p-6 border border-stone-100 animate-pulse"><div className="w-40 h-5 bg-stone-200 rounded mb-4" /><div className="w-full h-16 bg-stone-100 rounded" /></div>))}</div>
          ) : (
            <div className="space-y-4">
              {DOC_TYPES.map((dt) => {
                const docs = getDocsByType(dt.value);
                return (
                  <div key={dt.value} className="bg-white rounded-2xl border border-stone-100 p-5" data-testid={`docs-section-${dt.value}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <dt.icon className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-emerald-950 text-sm">{dt.label}</h3>
                        <p className="text-xs text-stone-400">{dt.desc}</p>
                      </div>
                    </div>
                    {docs.length === 0 ? (
                      <div className="bg-stone-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-stone-400">No document uploaded</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {docs.map((doc) => {
                          const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
                          return (
                            <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl" data-testid={`doc-${doc.doc_id}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <FileCheck className="w-4 h-4 text-stone-500 shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-stone-700 truncate">{doc.doc_name}</p>
                                  <p className="text-xs text-stone-400">{new Date(doc.uploaded_at).toLocaleDateString()}{doc.expiry_date && ` · Expires ${new Date(doc.expiry_date).toLocaleDateString()}`}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge className={sc.color}><sc.icon className="w-3 h-3 mr-1" />{sc.label}</Badge>
                                <button onClick={() => handleDelete(doc.doc_id)} className="p-1.5 text-stone-400 hover:text-red-500 transition-colors" data-testid={`delete-doc-${doc.doc_id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ProfessionalDocumentsPage;
